/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import {BaseFieldEditor} from "./BaseFieldEditor";
import autobind from "autobind-decorator";
import Debounce from "debounce-decorator";
import {ObjectUtils} from "../../../Utils/ObjectUtils";
import Autocomplete from "react-autocomplete";
import {StreamBankAutoCompleteItem} from "../../../Components/Common/StreamBankAutoCompleteItem";
import React from "react";

export function ServiceDrivenAutoCompleteFieldEditor(Service) {
	return class ServiceDrivenAutoCompleteFieldEditor extends BaseFieldEditor {

		constructor(props) {
			super(props);

			this.setInitialState({
				value: "", // the search term (note this is different than props.Value which is a PK)
				values: [], // the search results (to show in the dropdown menu)
				selectedModel: null, // the model result that corresponds to the selected value
			});

			this.previousSearch = {};
		}

		@autobind
		loadData() {
			super.loadData();

			if (this.props.Value) {
				Service.FindById(this.props.Value)
					.then((x) => {
						this.selectModel(x?.result, true);
					});
			}
		}

		@autobind
		shouldReloadData(prevProps, prevState) {
			return prevProps.Value !== this.props.Value && this.props.Value;
		}

		@autobind
		emptyMessage() {
			return this.props?.emptyMessage ?? "No matching results";
		}

		@autobind
		filterResults(values, selectedModel) {
			if (this.props?.CustomData?.filter) {
				return this.props.CustomData.filter(values, selectedModel);
			}
			return values;
		}

		@autobind
		selectModel(item, skipUpdate = false) {
			const {OnUpdate, OnUpdateModel} = this.props;
			this.setState({
				value: Service.GetTitle(item),
				selectedModel: item
			}, _ => {
				if (!skipUpdate) {
					OnUpdateModel?.(item);
					OnUpdate?.(Service.GetPrimaryKey(item));
				}
			});
		}

		@Debounce(250)
		performSearch() {
			const {value} = this.state || {};
			const {additionalSearchParams = {}} = this.props?.CustomData ?? this.props ?? {};
			const {context = null} = this.props;

			// don't perform a new search if our
			// criteria matches the selectedModel
			if (this.state.selectedModel && Service.GetTitle(this.state.selectedModel) === value) {
				return;
			}

			// don't repeat the last search
			// unless we have changed our criteria
			let currentSearch = {value, additionalSearchParams};
			if (ObjectUtils.Compare(currentSearch, this.previousSearch)) {
				return;
			}
			this.previousSearch = currentSearch;
			// let me = this;

			const searchResponseHandler = (x) => {
				this.setState({values: x.result});
			};
			const searchErrorHandler = (x) => {
				console.log(x);
			}

			const searchPromise = context ? Service.SearchWithContext(value, context, additionalSearchParams)
				: Service.Search(value, additionalSearchParams);

			searchPromise
				.then(searchResponseHandler)
				.catch(searchErrorHandler);
		}

		@autobind
		renderChildren(children) {
			if (this.props?.CustomData?.addItem) {
				let title;

				title = `new ${Service.ModelName}`;
				if (this.state.value) {
					title = `"${this.state.value}" as ` + title;
				}

				const createElement =
					<div key={'add-item-footer'} onClick={() => this.props.CustomData.addItem(this.state.value)}
					     className={'sb-autocomplete-item footer'}>
						+ Add {title}
					</div>;

				children.push(createElement)
			}

			if (children.length) {
				return children;
			}

			if (this.state.value === "") {
				return <div/>
			}

			return <div className="sb-autocomplete-no-items">
				{this.emptyMessage()}
			</div>;
		}

		@autobind
		onBlur(event) {
			if (this.state.value === '' || this.state.value == null) {
				this.selectModel(null);
			} else {
				this.setState({value: Service.GetTitle(this.state.selectedModel)});
			}
		}

		@autobind
		_render() {
			const subtitle = Service.GetSubtitle(this.state.selectedModel);
			const values = this.filterResults([...this.state.values], this.state.selectedModel);

			return <div className="autocomplete-column">
				<Autocomplete
					value={this.state.value}
					inputProps={{
						className: 'autocomplete-input form-control',
						placeholder: this.props.placeholder ?? Service.ModelName,
						onFocus: event => {
							event.target.setSelectionRange(0, -1);
							this.performSearch();
						},
						onBlur: this.onBlur
					}}
					wrapperStyle={{
						position: 'relative',
						display: 'inline-block'
					}}
					items={values}
					getItemValue={(item) => Service.GetTitle(item)}
					onChange={(event, value) => {
						this.setState({value: value}, this.performSearch);
					}}
					onSelect={(value, item) => {

						// don't trigger OnUpdate if the selection is the same
						if (Service.GetPrimaryKey(item) === Service.GetPrimaryKey(this.state.selectedModel)) {
							return;
						}

						this.selectModel(item);
					}}
					renderMenu={children => (
						<div className="sb-autocomplete-menu">
							{this.renderChildren(children)}
						</div>
					)}
					sortItems={Service.SortModels}
					renderItem={(item, isHighlighted) => {
						const key = Service.GetPrimaryKey(item);
						const title = Service.GetTitle(item);
						const subtitle = Service.GetSubtitle(item);
						const icon = Service.GetIcon(item);

						return <StreamBankAutoCompleteItem
							className="sb-autocomplete-item-taxLot"
							{...{ key, title, subtitle, icon }}
						/>
					}}
					// then add this, so you can override `autoComplete`
					renderInput={props => {
						return <input {...props} autoComplete="dont-autofill-this"/>;
					}}
				/>
				{subtitle != null ? <div className="sb-autocomplete-subtext">{subtitle}</div> : null}
			</div>;
		}
	};
}